import { useTranslation } from 'react-i18next';

export const HELP_CARD_TYPES = {
  NEED_REFRESHER: 'needRefresher',
  NEED_HELP: 'needHelp',
  HOW_IT_WORKS: 'howItWorks',
} as const;

export type HelpCardType = (typeof HELP_CARD_TYPES)[keyof typeof HELP_CARD_TYPES];

interface HelpCardProps {
  title: string;
  description: string;
  buttonText?: string;
  buttonLink?: string;
  externalLink?: boolean;
}

// Map the how-it-works article URL to the language code
const HOW_IT_WORKS_URLS: Record<string, string> = {
  en: 'https://help.c2fo.com/en/articles/13909296-how-it-works',
  de: 'https://help.c2fo.com/de/articles/13909296-so-funktioniert-es',
  es: 'http://help.c2fo.com/es/articles/13909296-como-funciona',
  fr: 'http://help.c2fo.com/fr/articles/13909296-comment-ca-marche',
  it: 'http://help.c2fo.com/it/articles/13909296-come-funziona',
  ja: 'http://help.c2fo.com/ja/articles/13909296-利用方法',
  nl: 'http://help.c2fo.com/nl/articles/13909296-hoe-het-werkt',
  'pt-BR': 'http://help.c2fo.com/pt-BR/articles/13909296-como-funciona',
  ru: 'http://help.c2fo.com/ru/articles/13909296-как-это-работает',
  tr: 'http://help.c2fo.com/tr/articles/13909296-nasil-calisir',
  'zh-CN': 'http://help.c2fo.com/zh-CN/articles/13909296-工作原理',
  'zh-TW': 'http://help.c2fo.com/zh-TW/articles/13909296-工作原理',
};

// define the content for each help card type
const HELP_CARD_CONTENT: Record<HelpCardType, HelpCardProps> = {
  [HELP_CARD_TYPES.NEED_REFRESHER]: {
    title: 'helpCard.needRefresher.title',
    description: 'helpCard.needRefresher.description',
    buttonText: 'helpCard.needRefresher.buttonText',
    buttonLink: 'https://help.c2fo.com/en/articles/11707327-experience-enhancements-as-of-july-2025',
    externalLink: true,
  },
  [HELP_CARD_TYPES.NEED_HELP]: {
    title: 'helpCard.needHelp.title',
    description: 'helpCard.needHelp.description',
    buttonText: 'helpCard.needHelp.buttonText',
    buttonLink: 'https://help.c2fo.com',
    externalLink: true,
  },
  [HELP_CARD_TYPES.HOW_IT_WORKS]: {
    title: 'helpCard.howItWorks.title',
    description: 'helpCard.howItWorks.description',
    buttonText: 'helpCard.howItWorks.buttonText',
    externalLink: true,
  },
} as const;

const HelpCard = ({ helpCardType }: { helpCardType: HelpCardType }) => {
  const { t, i18n } = useTranslation();
  const content = HELP_CARD_CONTENT[helpCardType];

  // Return the how it works article URL for the current language, or English if no article is available for the current language
  const buttonLink =
    helpCardType === HELP_CARD_TYPES.HOW_IT_WORKS
      ? HOW_IT_WORKS_URLS[i18n.language] ?? HOW_IT_WORKS_URLS['en']
      : content.buttonLink;

  return (
    <div className="flex flex-col justify-between rounded-xl border-[0.5px] border-stroke p-6">
      <div>
        <p className="font-bold">{t(content.title)}</p>
        <p className="text-sm text-text-secondary">{t(content.description)}</p>
      </div>
      {content.buttonText && buttonLink && (
        <div className="flex justify-end pt-2">
          <a href={buttonLink} className="font-bold text-secondary-500" target="_blank" rel="noreferrer">
            {t(content.buttonText)}
          </a>
        </div>
      )}
    </div>
  );
};

export default HelpCard;
